import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import { toast } from 'sonner';
import { logStructured } from '@/lib/networkLog';
import { trackAddToCart } from '@/lib/analytics/trackEvent';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const STORAGE_KEY = 'soluqr_cart';

/** @param {string | null | undefined} raw */
export function parsePersistedCart(raw) {
  if (!raw) return { cart: [], businessId: null };
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { cart: parsed, businessId: null };
    }
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.cart)) {
      const bid = parsed.businessId != null && String(parsed.businessId).trim() !== ''
        ? String(parsed.businessId).trim()
        : null;
      return { cart: parsed.cart, businessId: bid };
    }
  } catch {
    /* ignore */
  }
  return { cart: [], businessId: null };
}

function writePersistedCart(cart, businessId) {
  if (!cart.length) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      cart,
      businessId: businessId || null,
    })
  );
}

/** @param {unknown} x */
function normalizeCartProductIdKey(x) {
  if (x == null || x === '') return '';
  return String(x);
}

/** @param {{ product?: object }|null|undefined} line */
function lineBaseProductIdKeys(line) {
  const p = line?.product;
  if (!p || typeof p !== 'object') return [];
  const raw = [p.id, p.product_id, p._id].filter((k) => k != null && k !== '');
  return [...new Set(raw.map((k) => normalizeCartProductIdKey(k)))];
}

/**
 * @param {object[]} prev
 * @param {unknown} productId
 * @param {object[]} newAddOns
 * @param {{ cartLineId?: string|number|null }} [opts]
 * @returns {{ success: boolean, next: object[] }}
 */
export function tryMergeAddOnsIntoLatestLine(prev, productId, newAddOns, opts = {}) {
  const list = Array.isArray(prev) ? prev : [];
  if (!Array.isArray(newAddOns) || newAddOns.length === 0) {
    return { success: false, next: list };
  }

  const cartLineId = opts?.cartLineId != null && opts?.cartLineId !== '' ? String(opts.cartLineId) : '';
  const pid = normalizeCartProductIdKey(productId);

  let idx = -1;
  if (cartLineId) {
    for (let i = list.length - 1; i >= 0; i--) {
      if (String(list[i]?.id) === cartLineId) {
        idx = i;
        break;
      }
    }
  }
  if (idx < 0 && pid) {
    for (let i = list.length - 1; i >= 0; i--) {
      const keys = lineBaseProductIdKeys(list[i]);
      if (keys.includes(pid)) {
        idx = i;
        break;
      }
    }
  }

  if (idx < 0) {
    return { success: false, next: list };
  }

  const row = list[idx];
  const existing = row.addOns || [];
  const merged = [...existing];

  for (const a of newAddOns) {
    const name = String(a?.name || '').trim();
    const price = Number(a?.price) || 0;
    const qty = Math.max(1, Math.min(99, Number(a?.quantity) || 1));
    if (!name) continue;
    const dupIdx = merged.findIndex((m) => m.name === name && Number(m.price) === price);
    if (dupIdx >= 0) {
      const prevQ = Number(merged[dupIdx].quantity) || 1;
      merged[dupIdx] = {
        ...merged[dupIdx],
        quantity: Math.min(99, prevQ + qty),
      };
    } else {
      merged.push({ name, price, quantity: qty });
    }
  }

  const sameLen = merged.length === existing.length;
  const samePayload =
    sameLen &&
    merged.every((m, i) => {
      const e = existing[i];
      return (
        m.name === e.name &&
        Number(m.price) === Number(e.price) &&
        (Number(m.quantity) || 1) === (Number(e.quantity) || 1)
      );
    });
  if (samePayload) {
    return { success: false, next: list };
  }

  const next = [...list];
  next[idx] = {
    ...row,
    addOns: merged,
    price: calculateItemPrice(row.product, merged),
  };
  return { success: true, next };
}

function calculateItemPrice(product, addOns) {
  let price = Number(product?.final_price) || Number(product?.price) || 0;
  addOns.forEach((addon) => {
    const addonQty = Number(addon.quantity) || 1;
    price += (Number(addon.price) || 0) * addonQty;
  });
  return price;
}

function sumCartLines(lines) {
  return (Array.isArray(lines) ? lines : []).reduce((total, item) => {
    const itemPrice = Number(item.price) || 0;
    const itemQuantity = Number(item.quantity) || 1;
    return total + itemPrice * itemQuantity;
  }, 0);
}

function countCartLines(lines) {
  return (Array.isArray(lines) ? lines : []).reduce(
    (count, item) => count + (Number(item.quantity) || 1),
    0
  );
}

/**
 * Al entrar a rutas `/menu/:businessId`, alinea el carrito con ese local.
 * @param {string | undefined} businessId
 */
export function useSyncCartBusiness(businessId) {
  const { syncCartWithBusiness } = useContext(CartContext);
  useEffect(() => {
    if (businessId) syncCartWithBusiness(businessId);
  }, [businessId, syncCartWithBusiness]);
}

export const CartProvider = ({ children }) => {
  const [business, setBusiness] = useState(null);
  const [{ cart, businessId: cartBusinessId }, setCartState] = useState({
    cart: [],
    businessId: null,
  });
  const cartSnapshotRef = useRef([]);

  useLayoutEffect(() => {
    cartSnapshotRef.current = cart;
  }, [cart]);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    const { cart: lines, businessId: bid } = parsePersistedCart(raw);
    cartSnapshotRef.current = lines;
    setCartState({
      cart: lines,
      businessId: lines.length > 0 ? bid : null,
    });
  }, []);

  useEffect(() => {
    writePersistedCart(cart, cartBusinessId);
  }, [cart, cartBusinessId]);

  const syncCartWithBusiness = useCallback((businessId) => {
    const bid = String(businessId || '').trim();
    if (!bid) return;
    setCartState((s) => {
      if (s.cart.length === 0) return s;
      if (s.businessId !== bid) {
        cartSnapshotRef.current = [];
        return { cart: [], businessId: null };
      }
      return s;
    });
  }, []);

  /**
   * @returns {string|undefined} id único de la línea en el carrito (para merge por línea exacta).
   */
  const addToCart = (product, addOns = [], removedIngredients = [], quantity = 1, options = {}) => {
    const bid = String(options.businessId || '').trim();
    if (!bid) {
      console.warn('addToCart: `options.businessId` es obligatorio');
      return undefined;
    }
    const label = product?.name ?? 'Producto';
    const lineId = `${product?.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const cartItem = {
      id: lineId,
      product,
      addOns,
      removedIngredients,
      quantity,
      price: calculateItemPrice(product, addOns),
    };

    setCartState((s) => {
      let lines = s.cart;
      let owner = s.businessId;
      if (lines.length > 0 && owner && owner !== bid) {
        lines = [];
        owner = null;
      }
      if (lines.length === 0) {
        owner = bid;
      }
      const next = [...lines, cartItem];
      cartSnapshotRef.current = next;
      return { cart: next, businessId: owner };
    });
    const q = Number(quantity) || 1;
    logStructured('analytics', {
      event: 'product_added_to_cart',
      businessId: bid,
      product_id: product?.id != null && product?.id !== '' ? String(product.id) : '',
      quantity: q,
    });
    if (product?.id != null && product?.id !== '') {
      trackAddToCart(String(product.id), bid);
    }
    if (!options.silent) {
      toast.success(q > 1 ? `${q}× ${label} agregado al carrito` : `${label} agregado al carrito`);
    }
    return lineId;
  };

  /**
   * Línea de premio del club ya canjeada en backend: precio 0, visible como canje en checkout/WhatsApp.
   * @param {object} product — sin metadatos `assistantWelcomeKind`
   * @param {number} quantity
   * @param {object} [options]
   * @param {string} [options.clubRewardLabel]
   * @param {boolean} [options.silent]
   * @param {string} [options.businessId]
   */
  const addClubRewardToCart = (product, quantity = 1, addOns = [], removedIngredients = [], options = {}) => {
    const bid = String(options.businessId || '').trim();
    if (!bid) {
      console.warn('addClubRewardToCart: `options.businessId` es obligatorio');
      return undefined;
    }
    const qty = Math.max(1, Math.min(99, Number(quantity) || 1));
    const lineId = `${product?.id}-cr-${Date.now()}`;
    const cartItem = {
      id: lineId,
      product,
      addOns: addOns || [],
      removedIngredients: removedIngredients || [],
      quantity: qty,
      price: 0,
      isClubReward: true,
      clubRewardLabel: String(options.clubRewardLabel || '').trim() || undefined,
    };

    setCartState((s) => {
      let lines = s.cart;
      let owner = s.businessId;
      if (lines.length > 0 && owner && owner !== bid) {
        lines = [];
        owner = null;
      }
      if (lines.length === 0) {
        owner = bid;
      }
      const next = [...lines, cartItem];
      cartSnapshotRef.current = next;
      return { cart: next, businessId: owner };
    });
    if (!options.silent) {
      toast.success(
        qty > 1 ? `${qty} premios agregados a tu pedido` : 'Premio agregado a tu pedido'
      );
    }
    return lineId;
  };

  /**
   * Une agregados a la línea indicada por `cartLineId` (preferido) o a la última línea cuyo producto coincide con productId.
   * @param {unknown} productId
   * @param {object[]} newAddOns
   * @param {{ cartLineId?: string|number|null, businessId?: string }} [opts]
   */
  const mergeAddOnsIntoLatestLineForProduct = (productId, newAddOns = [], opts = {}) => {
    if (!Array.isArray(newAddOns) || newAddOns.length === 0) return false;
    const hasLine = opts?.cartLineId != null && String(opts.cartLineId) !== '';
    const hasPid = productId != null && String(productId) !== '';
    if (!hasLine && !hasPid) return false;

    let success = false;
    setCartState((s) => {
      const bidOpt = opts.businessId != null ? String(opts.businessId).trim() : '';
      if (bidOpt && s.businessId && bidOpt !== s.businessId) {
        return s;
      }
      const snapshot = cartSnapshotRef.current;
      let res = tryMergeAddOnsIntoLatestLine(snapshot, productId, newAddOns, opts);
      if (!res.success && hasLine && hasPid) {
        res = tryMergeAddOnsIntoLatestLine(snapshot, productId, newAddOns, { ...opts, cartLineId: null });
      }
      if (!res.success) return s;
      success = true;
      cartSnapshotRef.current = res.next;
      return {
        ...s,
        cart: res.next,
        businessId: res.next.length === 0 ? null : s.businessId,
      };
    });
    return success;
  };

  const removeFromCart = (itemId, opts = {}) => {
    setCartState((s) => {
      const next = s.cart.filter((item) => item.id !== itemId);
      cartSnapshotRef.current = next;
      return {
        cart: next,
        businessId: next.length === 0 ? null : s.businessId,
      };
    });
    if (!opts.silent) toast.success('Producto eliminado del carrito');
  };

  /**
   * Quita un agregado de una línea y recalcula precio unitario.
   * @param {string} itemId
   * @param {number} addOnIndex
   */
  const removeAddOnFromCartLine = (itemId, addOnIndex) => {
    setCartState((s) => {
      const next = s.cart.map((item) => {
        if (item.id !== itemId) return item;
        const addOns = [...(item.addOns || [])];
        if (addOnIndex < 0 || addOnIndex >= addOns.length) return item;
        addOns.splice(addOnIndex, 1);
        return {
          ...item,
          addOns,
          price: calculateItemPrice(item.product, addOns),
        };
      });
      cartSnapshotRef.current = next;
      return {
        cart: next,
        businessId: next.length === 0 ? null : s.businessId,
      };
    });
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId, { silent: true });
      return;
    }
    setCartState((s) => {
      const next = s.cart.map((item) => (item.id === itemId ? { ...item, quantity } : item));
      cartSnapshotRef.current = next;
      return {
        cart: next,
        businessId: next.length === 0 ? null : s.businessId,
      };
    });
  };

  const clearCart = () => {
    cartSnapshotRef.current = [];
    setCartState({ cart: [], businessId: null });
    localStorage.removeItem(STORAGE_KEY);
  };

  const getCartTotal = () => {
    return Math.max(
      sumCartLines(cartSnapshotRef.current),
      sumCartLines(cart)
    );
  };

  const getCartCount = () => {
    return Math.max(
      countCartLines(cartSnapshotRef.current),
      countCartLines(cart)
    );
  };

  const getCartSnapshot = () => cartSnapshotRef.current;

  const value = {
    cart,
    cartBusinessId,
    business,
    setBusiness,
    syncCartWithBusiness,
    addToCart,
    addClubRewardToCart,
    mergeAddOnsIntoLatestLineForProduct,
    removeFromCart,
    removeAddOnFromCartLine,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    getCartSnapshot,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
